// Integration types and enums

export enum IntegrationType {
  CROWDSTRIKE = 'crowdstrike',
  SENTINEL = 'sentinel',
  DEFENDER = 'defender',
}

// Integration Alert - matches IntegrationAlert.to_dict() from backend
export interface IntegrationAlert {
  id: string;
  integration_id: string;
  client_id: string;
  client_name: string | null;
  external_id: string;
  integration_type: string;
  title: string;
  description: string | null;
  severity: string | null;
  risk_score: number | null;
  status: string | null;
  hostname: string | null;
  ip_address: string | null;
  file_name: string | null;
  file_hash: string | null;
  mitre_tactic: string | null;
  mitre_technique: string | null;
  action_taken: string | null;
  username: string | null;
  detected_at: string | null;
  occurred_at: string | null;
  created_at: string;
  updated_at: string;
  ticket_id: string | null;
  raw_data?: any;
  // Enriched from local ATT&CK cache — null when table is empty or technique not found
  mitre_enriched: {
    technique_id: string;
    technique_name: string;
    tactic_id: string | null;
    tactic_name: string | null;
    tactic_shortname: string | null;
    is_sub_technique: boolean;
    parent_technique_id: string | null;
    url: string | null;
  } | null;
}

// Integration Incident
export interface IntegrationIncident {
  id: string;
  client_id: string;
  integration_id: string;
  integration_type: IntegrationType;
  external_id: string;
  title: string;
  description: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  created_at_source: string;
  created_at: string;
  updated_at: string;
  raw_data?: any;
}

// Integration Device
export interface IntegrationDevice {
  id: string;
  client_id: string;
  integration_id: string;
  integration_type: IntegrationType;
  external_id: string;
  hostname: string;
  os_platform: string | null;
  os_version: string | null;
  ip_addresses: string[] | null;
  mac_addresses: string[] | null;
  is_online: boolean;
  health_status: string | null;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
  raw_data?: any;
}

// Integration Summary
export interface IntegrationSummary {
  alerts: {
    total: number;
    by_severity: {
      [key: string]: number;
    };
  };
  incidents: {
    total: number;
    by_status: {
      [key: string]: number;
    };
  };
  devices: {
    total: number;
    online: number;
    offline: number;
  };
}

// Filter types for API calls
export interface IntegrationAlertFilters {
  client_id?: string;
  integration_id?: string;
  integration_type?: IntegrationType;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export interface IntegrationIncidentFilters {
  client_id?: string;
  integration_id?: string;
  integration_type?: IntegrationType;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export interface IntegrationDeviceFilters {
  client_id?: string;
  integration_id?: string;
  integration_type?: IntegrationType;
  health_status?: string;
  is_online?: boolean;
  os_platform?: string;
  limit?: number;
  offset?: number;
}

// Response types
export interface IntegrationAlertsResponse {
  alerts: IntegrationAlert[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface IntegrationIncidentsResponse {
  incidents: IntegrationIncident[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface IntegrationDevicesResponse {
  devices: IntegrationDevice[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

// Redux state
export interface IntegrationDataState {
  alerts: IntegrationAlert[];
  selectedAlert: IntegrationAlert | null;
  alertsPagination: {
    total: number;
    limit: number;
    offset: number;
  };

  incidents: IntegrationIncident[];
  selectedIncident: IntegrationIncident | null;
  incidentsPagination: {
    total: number;
    limit: number;
    offset: number;
  };

  devices: IntegrationDevice[];
  selectedDevice: IntegrationDevice | null;
  devicesPagination: {
    total: number;
    limit: number;
    offset: number;
  };

  summary: IntegrationSummary | null;

  isLoading: boolean;
  error: string | null;
}
